import { format } from "date-fns";
import { DropdownMenu, Icon, IconButton, Typography } from "@/components/atoms";
import { DataTableColumnDef } from "@/components/molecules";
import { SubscriptionBillingHistoryItem } from "@/redux/apis/subscription/types";
import { formatCurrency } from "@/utils";
import { SubscriptionBillingHistoryTableData } from "./types";

// Re-export for backwards compatibility
export type SubscriptionBillingItem = SubscriptionBillingHistoryItem;

/**
 * Render the download control for a billing row:
 * - one document  -> a single download button (defaults to the latest document)
 * - many documents -> a dropdown listing them newest-first (Invoice, Receipt,
 *   Credit Note, Refund Receipt), each downloading on click
 */
const renderDownloadCell = (item: SubscriptionBillingItem, onDownload: (documentUrl?: string) => void) => {
  const documents = item.documents ?? [];

  if (documents.length <= 1) {
    const only = documents[0];
    const url = only?.url ?? item.documentUrl;
    return <IconButton variant="custom" iconSize="lg" iconName="ImportCurve" onClick={() => onDownload(url)} disabled={!url} />;
  }

  return (
    <DropdownMenu position="bottom-end" className="!flex max-w-[24px]" button={<Icon name="ImportCurve" size="lg" />}>
      {documents.map((doc) => (
        <DropdownMenu.Item key={doc.id} onClick={() => onDownload(doc.url)} className="w-[220px]">
          <Icon name="ImportCurve" size="sm" className="mr-2.5" />
          <Typography>{doc.label}</Typography>
        </DropdownMenu.Item>
      ))}
    </DropdownMenu>
  );
};

export const generateColumns = () => {
  const columns: DataTableColumnDef<SubscriptionBillingHistoryTableData>[] = [
    {
      accessorKey: "subscriptionBillings",
      header: "Subscription Billings",
      cell: (props) => props.row.original.subscriptionBillings,
      enableSorting: false,
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: (props) => props.row.original.date,
      enableSorting: false,
      size: 150,
    },
    {
      accessorKey: "time",
      header: "Time",
      cell: (props) => props.row.original.time,
      enableSorting: false,
      size: 120,
    },
    {
      accessorKey: "total",
      header: "Total",
      cell: (props) => props.row.original.total,
      enableSorting: false,
      size: 120,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (props) => props.row.original.status,
      enableSorting: false,
      size: 100,
    },
    {
      accessorKey: "download",
      header: "",
      cell: (props) => props.row.original.download,
      enableSorting: false,
      size: 80,
    },
  ];

  return columns;
};

export const generateData = (items: SubscriptionBillingItem[], dateFormat: string, onDownload: (documentUrl?: string) => void) => {
  const data = items.map(
    (i) =>
      ({
        subscriptionBillings: i.planName,
        date: format(new Date(i.date), dateFormat),
        time: format(new Date(i.date), "h:mm a"),
        total: formatCurrency(i.amount, 2, i.currency),
        status: i.status,
        download: renderDownloadCell(i, onDownload),
      } as SubscriptionBillingHistoryTableData)
  );

  return data;
};
